import { effect, inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { finalize, forkJoin, interval, Observable, take, tap } from 'rxjs';
import { LoadingContent, LoadingIdsEnum, LoadingTypeEnum } from '../models/loading.model';
import {
  activeDefaultLoadingAction,
  setLoadingActive,
  setLoadingContent,
  setLoadingDisabled,
} from '../store/states/config/config.actions';
import { selectConfigLoading, selectLoading } from '../store/states/config/config.selectors';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnpackedObservables<T extends readonly Observable<any>[]> = {
  [K in keyof T]: T[K] extends Observable<infer R> ? R : never;
};

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private readonly store = inject(Store);
  progressBarPercentage = signal<number>(1);

  constructor() {
    effect(() => {
      if (!this.store.selectSignal(selectLoading)()) {
        this.progressBarPercentage.set(1);
      }
    });
  }

  makeLoadingPay() {
    this.store.dispatch(
      setLoadingActive({
        loadingConfig: {
          id: LoadingIdsEnum.PROGRESS_BAR,
          type: LoadingTypeEnum.BlurBackground,
          content: [
            {
              title: 'Quase pronto!',
              subtitle: 'O comprovante será exibido após a aprovação',
            },
            {
              title: 'Processando o seu pagamento',
              subtitle: 'Isso pode levar alguns segundos',
            },
            {
              title: 'Não feche esta tela',
              subtitle: 'Aguarde para acompanhar a aprovação',
            },
          ],
        },
      })
    );
    return this;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showLoadingUntilCompleted<T extends Observable<any>[]>(
    sources: T
  ): Observable<UnpackedObservables<T>> {
    const isLoadingAlreadyExists = this.store.selectSignal(selectConfigLoading)().loading;
    if (!isLoadingAlreadyExists) this.store.dispatch(activeDefaultLoadingAction());

    return forkJoin(sources).pipe(
      finalize(() => {
        this.store.dispatch(setLoadingDisabled());
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any;
  }

  disableLoadingWithSuccessMessage(content: LoadingContent, intervalInMiliSeconds: number) {
    this.progressBarPercentage.set(100);
    this.store.dispatch(setLoadingContent({ content: [content] }));
    return interval(intervalInMiliSeconds).pipe(
      take(1),
      tap(() => {
        this.store.dispatch(setLoadingDisabled());
      })
    );
  }
}
